import React from 'react';
import Icon from '../../../components/AppIcon';

const VerificationSummary = ({ recipients }) => {
  const verificationStats = recipients?.reduce((acc, recipient) => {
    acc[recipient.verificationStatus] = (acc?.[recipient?.verificationStatus] || 0) + 1;
    return acc;
  }, {});

  const totalRecipients = recipients?.length;
  const verifiedCount = verificationStats?.verified || 0;
  const pendingCount = verificationStats?.pending || 0;
  const failedCount = verificationStats?.failed || 0;

  const verificationRate = totalRecipients > 0 ? Math.round((verifiedCount / totalRecipients) * 100) : 0;

  const summaryItems = [
    {
      label: 'Total Recipients',
      value: totalRecipients,
      icon: 'Users',
      color: 'text-foreground bg-muted'
    },
    {
      label: 'Verified',
      value: verifiedCount,
      icon: 'CheckCircle',
      color: 'text-success bg-success/10'
    },
    {
      label: 'Pending Verification',
      value: pendingCount,
      icon: 'Clock',
      color: 'text-warning bg-warning/10'
    },
    {
      label: 'Failed Verification',
      value: failedCount,
      icon: 'XCircle',
      color: 'text-error bg-error/10'
    }
  ];

  const pendingRecipients = recipients?.filter(r => r?.verificationStatus === 'pending');
  const failedRecipients = recipients?.filter(r => r?.verificationStatus === 'failed');

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">Verification Summary</h3>
        <div className="text-right">
          <p className="text-2xl font-bold text-foreground">{verificationRate}%</p>
          <p className="text-sm text-muted-foreground">Verification Rate</p>
        </div>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        {summaryItems?.map((item) => (
          <div key={item?.label} className="flex items-center space-x-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${item?.color}`}>
              <Icon name={item?.icon} size={20} />
            </div>
            <div>
              <p className="text-lg font-semibold text-foreground">{item?.value}</p>
              <p className="text-sm text-muted-foreground">{item?.label}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Alerts */}
      {(pendingCount > 0 || failedCount > 0) && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">Attention Required</h4>
          
          {pendingCount > 0 && (
            <div className="flex items-start space-x-3 p-3 bg-warning/10 border border-warning/20 rounded-lg">
              <Icon name="Clock" size={16} className="text-warning mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-warning">
                  {pendingCount} recipient{pendingCount > 1 ? 's' : ''} pending verification
                </p>
                <div className="mt-1 space-y-1">
                  {pendingRecipients?.slice(0, 3)?.map((recipient) => (
                    <p key={recipient?.id} className="text-xs text-muted-foreground">
                      • {recipient?.name} ({recipient?.country})
                    </p>
                  ))}
                  {pendingRecipients?.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      • And {pendingRecipients?.length - 3} more...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {failedCount > 0 && (
            <div className="flex items-start space-x-3 p-3 bg-error/10 border border-error/20 rounded-lg">
              <Icon name="XCircle" size={16} className="text-error mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error">
                  {failedCount} recipient{failedCount > 1 ? 's' : ''} failed verification
                </p>
                <div className="mt-1 space-y-1">
                  {failedRecipients?.slice(0, 3)?.map((recipient) => (
                    <p key={recipient?.id} className="text-xs text-muted-foreground">
                      • {recipient?.name} ({recipient?.country})
                    </p>
                  ))}
                  {failedRecipients?.length > 3 && (
                    <p className="text-xs text-muted-foreground">
                      • And {failedRecipients?.length - 3} more...
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VerificationSummary;